
import React, { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  PlusCircle,
  ChevronLeft,
  ChevronRight,
  Trash2,
  ImagePlus,
  SaveIcon
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

interface PortfolioItem {
  id: string;
  title: string;
  description: string | null;
  image_url: string;
}

interface UserPortfolioModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  canEdit: boolean;
}

const UserPortfolioModal: React.FC<UserPortfolioModalProps> = ({ 
  isOpen, 
  onClose,
  userId,
  canEdit 
}) => {
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // New item form state
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newImageFile, setNewImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  
  useEffect(() => {
    const fetchPortfolioItems = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("user_portfolio")
          .select("*")
          .eq("user_id", userId)
          .order("display_order", { ascending: true });
        
        if (error) throw error;
        setPortfolioItems(data || []);
      } catch (error) {
        toast.error("Erro ao carregar portfólio");
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPortfolioItems();
  }, [userId]);
  
  useEffect(() => {
    // Clear preview when component unmounts
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : portfolioItems.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < portfolioItems.length - 1 ? prev + 1 : 0));
  };
  
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewImageFile(file);
      
      // Create preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };
  
  const handleAddPortfolioItem = async () => {
    if (!newTitle || !newImageFile) return;
    
    setIsSaving(true);
    try {
      // 1. Upload the image to storage
      const fileExt = newImageFile.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `${userId}/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from("portfolio")
        .upload(filePath, newImageFile);
        
      if (uploadError) throw uploadError;
      
      // 2. Get the public URL
      const { data: publicURLData } = await supabase.storage
        .from("portfolio")
        .getPublicUrl(filePath);
      
      // 3. Save the portfolio item to the database
      const newPortfolioItem = {
        user_id: userId,
        title: newTitle,
        description: newDescription,
        image_url: publicURLData.publicUrl,
        display_order: portfolioItems.length
      };
      
      const { data, error } = await supabase
        .from("user_portfolio")
        .insert(newPortfolioItem)
        .select()
        .single();
        
      if (error) throw error;
      
      setPortfolioItems([...portfolioItems, data]);
      setCurrentIndex(portfolioItems.length);
      
      // Reset form
      setNewTitle("");
      setNewDescription("");
      setNewImageFile(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl("");
      setIsAdding(false);
      
      toast.success("Trabalho adicionado com sucesso");
    } catch (error) {
      toast.error("Erro ao adicionar trabalho ao portfólio");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDeletePortfolioItem = async (id: string) => {
    try {
      // Encontrar o item para poder remover a imagem do storage
      const itemToDelete = portfolioItems.find(item => item.id === id);
      
      if (itemToDelete) {
        // Extrair o caminho do arquivo da URL
        const imageUrl = itemToDelete.image_url;
        const filePathMatch = imageUrl.match(/portfolio\/([^?]+)/);
        
        if (filePathMatch && filePathMatch[1]) {
          const filePath = filePathMatch[1];
          // Remover a imagem do storage
          await supabase.storage
            .from("portfolio")
            .remove([filePath]);
        }
      }
      
      const { error } = await supabase
        .from("user_portfolio")
        .delete()
        .eq("id", id);
        
      if (error) throw error;
      
      const updatedItems = portfolioItems.filter(item => item.id !== id);
      setPortfolioItems(updatedItems);
      
      if (currentIndex >= updatedItems.length) {
        setCurrentIndex(updatedItems.length > 0 ? updatedItems.length - 1 : 0);
      }
      
      toast.success("Trabalho removido com sucesso");
    } catch (error) {
      toast.error("Erro ao remover trabalho");
      console.error(error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Trabalhos Realizados</DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              {isAdding ? (
                <motion.div
                  key="add-form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-4"
                >
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="w-full md:w-1/2">
                      <div className="relative border-2 border-dashed rounded-lg h-64 flex flex-col items-center justify-center">
                        {previewUrl ? (
                          <div className="relative w-full h-full">
                            <img 
                              src={previewUrl} 
                              className="object-contain w-full h-full p-2" 
                              alt="Preview" 
                            />
                            <Button
                              variant="destructive"
                              size="sm"
                              className="absolute top-2 right-2"
                              onClick={() => {
                                if (previewUrl) {
                                  URL.revokeObjectURL(previewUrl);
                                }
                                setPreviewUrl("");
                                setNewImageFile(null);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <label className="cursor-pointer flex flex-col items-center gap-2">
                            <ImagePlus className="h-10 w-10 text-muted-foreground" />
                            <span className="text-muted-foreground text-sm">Clique para adicionar imagem</span>
                            <Input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={handleImageChange}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                    <div className="w-full md:w-1/2 space-y-4">
                      <div>
                        <label className="text-sm font-medium" htmlFor="title">
                          Título
                        </label>
                        <Input
                          id="title"
                          value={newTitle}
                          onChange={(e) => setNewTitle(e.target.value)}
                          placeholder="Título do trabalho"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium" htmlFor="description">
                          Descrição
                        </label>
                        <Textarea
                          id="description"
                          value={newDescription}
                          onChange={(e) => setNewDescription(e.target.value)}
                          placeholder="Descreva o trabalho realizado"
                          className="h-32"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-2 pt-2">
                    <Button 
                      variant="outline"
                      onClick={() => setIsAdding(false)}
                      disabled={isSaving}
                    >
                      Cancelar
                    </Button>
                    <Button 
                      onClick={handleAddPortfolioItem}
                      disabled={!newTitle || !newImageFile || isSaving}
                    >
                      {isSaving ? (
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      ) : (
                        <SaveIcon className="h-4 w-4 mr-2" />
                      )}
                      Salvar
                    </Button>
                  </div>
                </motion.div>
              ) : portfolioItems.length > 0 ? (
                <motion.div
                  key="portfolio-slider"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="relative"
                >
                  <div className="relative h-96">
                    <motion.div
                      key={currentIndex}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 flex flex-col md:flex-row gap-4"
                    >
                      <div className="w-full md:w-1/2 h-64 md:h-auto rounded-lg overflow-hidden bg-muted">
                        <img 
                          src={portfolioItems[currentIndex].image_url}
                          alt={portfolioItems[currentIndex].title}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      
                      <div className="w-full md:w-1/2 p-4">
                        <h3 className="text-xl font-bold mb-2">
                          {portfolioItems[currentIndex].title}
                        </h3>
                        <p className="text-muted-foreground">
                          {portfolioItems[currentIndex].description || "Sem descrição"}
                        </p>
                        
                        {canEdit && (
                          <div className="mt-4">
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeletePortfolioItem(portfolioItems[currentIndex].id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remover Trabalho
                            </Button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  </div>
                  
                  <div className="flex justify-center mt-4 gap-2">
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={handlePrevious}
                      disabled={portfolioItems.length <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {portfolioItems.map((_, index) => (
                        <div 
                          key={index}
                          className={`h-2 w-2 rounded-full ${
                            index === currentIndex ? "bg-primary" : "bg-muted"
                          }`}
                          onClick={() => setCurrentIndex(index)}
                        />
                      ))}
                    </div>
                    
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={handleNext}
                      disabled={portfolioItems.length <= 1}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="empty-state"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center py-12"
                >
                  <div className="text-muted-foreground text-center">
                    <p className="mb-4">Nenhum trabalho adicionado ao portfólio</p>
                    {canEdit && (
                      <Button onClick={() => setIsAdding(true)}>
                        <PlusCircle className="h-4 w-4 mr-2" />
                        Adicionar Trabalho
                      </Button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>
        
        <DialogFooter>
          {canEdit && !isAdding && portfolioItems.length > 0 && (
            <Button onClick={() => setIsAdding(true)} className="mr-auto">
              <PlusCircle className="h-4 w-4 mr-2" />
              Adicionar Novo Trabalho
            </Button>
          )}
          <Button onClick={onClose} variant="outline">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserPortfolioModal;
